import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  BadgeCheckIcon,
  CameraIcon,
  ImageIcon,
  Loader2Icon,
  SquareUserIcon,
} from "lucide-react"
import { useCallback, useMemo } from "react"
import { useDropzone } from "react-dropzone"
import {
  CardIdVerificationStatus,
  getCardIdVerificationStatus,
  uploadCardFile,
} from "@/api/api"
import { NextStep } from "@/components/next-step"
import { PageLoader } from "@/components/page-loader"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { useCurrentToken } from "@/lib/use-current-token"
import { cn } from "@/lib/utils"

const CardUploader = ({
  status,
  side,
  token_id,
}: {
  status: CardIdVerificationStatus
  side: "card_id_front" | "card_id_rear"
  token_id: string
}) => {
  const queryClient = useQueryClient()
  const uploadMutation = useMutation({
    mutationKey: ["uploadCardFile"],
    mutationFn: uploadCardFile,
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ["getCardIdStatus", { token_id }],
      })
    },
  })

  const imagePreview = useMemo(() => {
    let fileUrl
    if (side === "card_id_front") {
      fileUrl = status.front_side_url
    } else if (side === "card_id_rear") {
      fileUrl = status.rear_side_url
    }

    return fileUrl ? (
      <img src={fileUrl} alt={side} className="h-full w-full rounded-md" />
    ) : null
  }, [status, side])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: !!imagePreview,
    multiple: false,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    onDrop: useCallback(
      async (files: File[]) => {
        uploadMutation.mutate({
          kind: side,
          file: files[0],
        })
      },
      [side, uploadMutation],
    ),
  })

  if (uploadMutation.isPending) {
    return (
      <div className="m-4 flex w-full flex-col items-center gap-3 rounded-md bg-stone-200 py-10 dark:bg-stone-700">
        <Loader2Icon className="animate-spin" size={48} />
        <div>Subiendo imagen</div>
      </div>
    )
  }

  return (
    <div
      {...getRootProps()}
      className={cn(
        "flex w-full flex-col items-center gap-2 rounded-md border-2 border-dashed border-transparent transition-all",
        !imagePreview && [
          "h-80 cursor-pointer justify-center bg-stone-200 dark:bg-stone-700",
          "hover:bg-stone-200/80 dark:hover:bg-stone-700/80",
        ],
        isDragActive && "border-zinc-400",
      )}
    >
      <input {...getInputProps()} type="file" className="hidden" />

      {imagePreview}

      {isDragActive && !imagePreview ? (
        <div className="flex flex-col items-center gap-4">
          <ImageIcon className="mr-3" size={32} />
          <span>Suelta aquí el archivo</span>
        </div>
      ) : (
        <>
          <Button
            size="sm"
            variant="outline"
            onClick={() => imagePreview && open()}
          >
            <CameraIcon className="mr-2" />
            <span>
              {imagePreview ? "Cambiar" : "Seleccionar"} imagen{" "}
              {side === "card_id_front" ? "lado frontal" : "lado reverso"}
            </span>
          </Button>

          {!imagePreview && (
            <span className="hidden text-sm md:block">
              o arrastra tu archivo aquí
            </span>
          )}
        </>
      )}
    </div>
  )
}

const CardVerificator = ({
  status,
  token_id,
}: {
  status: CardIdVerificationStatus
  token_id: string
}) => {
  const isVerified =
    status.status !== "unverified" &&
    !!status.rear_side_url &&
    !!status.front_side_url

  if (isVerified) {
    return (
      <>
        <BadgeCheckIcon size={64} className="text-primary" />

        <p className="text-center">
          ¡Tu cédula de identidad se ha verificado exitosamente!
        </p>

        <NextStep />
      </>
    )
  }

  return (
    <>
      <p>Sube una imagen de tu cédula de identidad por ambos lados</p>
      <CardUploader status={status} token_id={token_id} side="card_id_front" />
      <CardUploader status={status} token_id={token_id} side="card_id_rear" />
    </>
  )
}

const CardIdVerifyPage = () => {
  const { token_id, isReady } = useCurrentToken()

  const statusQuery = useQuery({
    queryKey: ["getCardIdStatus", { token_id }],
    queryFn: () => getCardIdVerificationStatus(),
    enabled: isReady,
    refetchOnWindowFocus: false,
  })

  if (!isReady || statusQuery.isLoading) {
    return <PageLoader />
  }

  if (statusQuery.isError) {
    throw statusQuery.error
  }

  if (!statusQuery.isSuccess || !statusQuery.data) {
    throw new Error("Error inesperado")
  }

  return (
    <div className="mx-auto md:w-10/12 lg:w-8/12">
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex flex-col items-center justify-center md:flex-row">
            <SquareUserIcon className="md:mr-3" />
            <h1 className="text-center text-xl md:text-2xl">
              Documento de identidad
            </h1>
          </div>
        </CardHeader>

        <CardContent className="flex flex-col items-center justify-center gap-6">
          <CardVerificator status={statusQuery.data} token_id={token_id} />
        </CardContent>
      </Card>
    </div>
  )
}

export { CardIdVerifyPage }
